import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import api, { errMsg } from '../lib/api';
import { PageHead, Modal, Field, Sel, Tgl, ImagePicker, inp } from './components';
import { fmtDate, JOURNAL_CATEGORIES } from '../lib/shop';

const EMPTY = {
  title: '', slug: '', category: 'Research Education', tags: [], excerpt: '', content: '',
  author: 'VQ Health Editorial', reading_time: 5, hero_image: '', status: 'draft',
  publish_at: '', seo_title: '', seo_description: '',
};

export default function AdminJournal() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/journal', { params: q ? { q } : {} });
      setItems(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, [q]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, publish_at: form.publish_at || new Date().toISOString(), reading_time: Number(form.reading_time) || 5 };
      if (editing?.id) await api.put(`/admin/journal/${editing.id}`, payload);
      else await api.post('/admin/journal', payload);
      toast.success(form.status === 'published' ? 'Article published — live at /journal' : 'Article saved');
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    await api.delete(`/admin/journal/${a.id}`);
    load();
  };

  return (
    <div data-testid="admin-journal">
      <PageHead title="Journal" sub="Research Journal CMS — publish without touching code.">
        <button data-testid="add-article" onClick={() => { setForm({ ...EMPTY }); setEditing({}); }}
          className="bg-[#0B0F12] text-white px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-widest flex items-center gap-1.5 hover:bg-[#E54D2E] transition-colors">
          <Plus className="w-3.5 h-3.5" /> New Article
        </button>
      </PageHead>

      <div className="flex items-center gap-2 border hairline bg-white px-3 py-2.5 mb-4 max-w-sm">
        <Search className="w-4 h-4 text-[#8A8F95]" />
        <input data-testid="journal-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…" className="flex-1 text-sm focus:outline-none" />
      </div>

      <div className="border hairline bg-white overflow-x-auto">
        <table className="w-full min-w-[700px] text-sm">
          <thead><tr className="border-b hairline">{['Title', 'Category', 'Status', 'Published', ''].map((h) => <th key={h} className="text-left p-3 font-mono2 text-[10px] uppercase tracking-widest text-[#8A8F95]">{h}</th>)}</tr></thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-b hairline last:border-0">
                <td className="p-3 font-semibold">{a.title}</td>
                <td className="p-3 font-mono2 text-xs">{a.category}</td>
                <td className="p-3"><span className={`font-mono2 text-[10px] uppercase tracking-wider border px-2 py-0.5 ${a.status === 'published' ? 'border-[#6EE7B7] bg-[#ECFDF5]' : 'hairline'}`}>{a.status}</span></td>
                <td className="p-3 text-xs text-[#8A8F95]">{fmtDate(a.publish_at)}</td>
                <td className="p-3">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`article-edit-${a.id}`} onClick={() => { setForm({ ...EMPTY, ...a }); setEditing(a); }} className="p-1.5 hover:bg-[#F4F4F0]"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`article-delete-${a.id}`} onClick={() => remove(a)} className="p-1.5 hover:bg-[#F4F4F0] text-[#E54D2E]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#8A8F95]">No articles</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal title={editing.id ? 'Edit Article' : 'New Article'} onClose={() => setEditing(null)} wide>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Field label="Title *"><input data-testid="article-title" value={form.title} onChange={(e) => set('title', e.target.value)} className={inp} /></Field></div>
            <Field label="Slug (auto if blank)"><input data-testid="article-slug" value={form.slug} onChange={(e) => set('slug', e.target.value)} className={inp} /></Field>
            <Sel label="Category" value={form.category} onChange={(v) => set('category', v)} options={JOURNAL_CATEGORIES} testid="article-category" />
            <Field label="Author"><input data-testid="article-author" value={form.author} onChange={(e) => set('author', e.target.value)} className={inp} /></Field>
            <Field label="Reading time (min)"><input data-testid="article-reading-time" type="number" value={form.reading_time} onChange={(e) => set('reading_time', e.target.value)} className={inp} /></Field>
            <Field label="Tags (comma separated)"><input data-testid="article-tags" value={(form.tags || []).join(', ')} onChange={(e) => set('tags', e.target.value.split(',').map((t) => t.trim()).filter(Boolean))} className={inp} /></Field>
            <Sel label="Status" value={form.status} onChange={(v) => set('status', v)} options={[['draft', 'Draft'], ['published', 'Published'], ['scheduled', 'Scheduled']]} testid="article-status" />
            <Field label="Publish date/time"><input data-testid="article-publish-at" value={form.publish_at} onChange={(e) => set('publish_at', e.target.value)} className={inp} placeholder="ISO date — blank = now" /></Field>
            <div className="sm:col-span-2"><ImagePicker label="Hero image" value={form.hero_image} onChange={(v) => set('hero_image', v)} testid="article-hero" /></div>
            <div className="sm:col-span-2"><Field label="Excerpt"><textarea data-testid="article-excerpt" rows={2} value={form.excerpt} onChange={(e) => set('excerpt', e.target.value)} className={inp} /></Field></div>
            <div className="sm:col-span-2"><Field label="Content (markdown: ## headings, **bold**, - lists)"><textarea data-testid="article-content" rows={14} value={form.content} onChange={(e) => set('content', e.target.value)} className={`${inp} font-mono2 text-xs`} /></Field></div>
            <Field label="SEO title"><input data-testid="article-seo-title" value={form.seo_title} onChange={(e) => set('seo_title', e.target.value)} className={inp} /></Field>
            <Field label="SEO description"><input data-testid="article-seo-description" value={form.seo_description} onChange={(e) => set('seo_description', e.target.value)} className={inp} /></Field>
          </div>
          <div className="flex gap-2 mt-6 pt-4 border-t hairline">
            <button data-testid="article-save" onClick={save} disabled={saving}
              className="bg-[#0B0F12] text-white px-6 py-3 font-mono2 text-[10px] uppercase tracking-widest hover:bg-[#1F8A58] transition-colors disabled:opacity-50">
              {saving ? 'Saving…' : 'Save article'}
            </button>
            <button onClick={() => setEditing(null)} className="border hairline px-6 py-3 font-mono2 text-[10px] uppercase tracking-widest">Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
